import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosError } from 'axios'
import momiAPI from '../service/momiAPI'
import { Momi, MomiPatch } from '../types/momiTypes'

export type CallbackFunction = (result: { result?: any; error?: any }) => void

export const useMomis = ({
  page = 0,
  pageSize = 50,
  search = '',
}: { page?: number; pageSize?: number; search?: string } = {}) => {
  const allMomisQuery = useQuery({
    queryKey: ['momis', page, pageSize, search],
    queryFn: () => momiAPI.getAllMomis(page, pageSize, search),
  })

  return {
    data: allMomisQuery.data,
    isLoading: allMomisQuery.isLoading,
    isError: allMomisQuery.isError,
    error: allMomisQuery.error as AxiosError,
    refetch: allMomisQuery.refetch,
  }
}

export const useCreateMomi = () => {
  const queryClient = useQueryClient()

  const createMomi = useMutation({
    mutationFn: ({
      momi,
      callbackFunc,
    }: {
      momi: MomiPatch
      callbackFunc?: CallbackFunction
    }) =>
      momiAPI.createMomi(momi).then((result) => {
        queryClient.invalidateQueries('momis')
        if (callbackFunc) {
          callbackFunc({ result, error: undefined })
        }
      }).catch((error) => {
        if (callbackFunc) {
          callbackFunc({ result: undefined, error })
        }
        return Promise.reject(error)
      }),
    onSettled: async () => {
      queryClient.invalidateQueries('momis')
    },
  })

  return {
    mutate: createMomi.mutate,
    isLoading: createMomi.isLoading,
    isSuccess: createMomi.isSuccess,
    isError: createMomi.isError,
    error: createMomi.error as AxiosError,
  }
}

export const useUpdateMomi = () => {
  const queryClient = useQueryClient()

  const updateMomi = useMutation({
    mutationFn: ({
      momiId,
      momi,
      callbackFunc,
    }: {
      momiId: string
      momi: Momi
      callbackFunc?: CallbackFunction
    }) =>
      momiAPI.updateMomi(momiId, momi).then((result) => {
        queryClient.invalidateQueries('momis')
        if (callbackFunc) {
          callbackFunc({ result, error: undefined })
        }
      }).catch((error) => {
        if (callbackFunc) {
          callbackFunc({ result: undefined, error })
        }
        return Promise.reject(error)
      }),
    onSettled: async () => {
      queryClient.invalidateQueries('momis')
    },
  })

  return {
    mutate: updateMomi.mutate,
    isLoading: updateMomi.isLoading,
    isSuccess: updateMomi.isSuccess,
    isError: updateMomi.isError,
    error: updateMomi.error as AxiosError,
  }
}

export const useDeleteMomi = () => {
  const queryClient = useQueryClient()

  const deleteMomi = useMutation({
    mutationFn: ({
      momiId,
      callbackFunc,
    }: {
      momiId: string
      callbackFunc?: CallbackFunction
    }) =>
      momiAPI.deleteMomi(momiId).then((result) => {
        queryClient.invalidateQueries('momis')
        if (callbackFunc) {
          callbackFunc({ result, error: undefined })
        }
      }).catch((error) => {
        if (callbackFunc) {
          callbackFunc({ result: undefined, error })
        }
        return Promise.reject(error)
      }),
    onSettled: async () => {
      queryClient.invalidateQueries('momis')
    },
  })

  return {
    mutate: deleteMomi.mutate,
    isLoading: deleteMomi.isLoading,
    isSuccess: deleteMomi.isSuccess,
    isError: deleteMomi.isError,
    error: deleteMomi.error as AxiosError,
  }
}
